import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import GlobalUserNavigation, {DEFAULT_IDEXPRESS_THEME, persistLanguagePreference, themeTokensFromEnvironment, useIdExpressGlobalUserNavigation} from "@id-express/global-user-navigation";
import {useTranslation} from "react-i18next";
import {api} from "./api";
import {trustedAvatarConsentUrl} from "./currentAvatar";
import {normalizeActivityLogRows} from "./activityLogView";
import AdministratorsPanel from "./components/AdministratorsPanel";
import Breadcrumbs from "./components/Breadcrumbs";
import DataTable from "./components/DataTable";
import CachePanel from "./components/CachePanel";
import DictionaryPanel, {type DictionaryFormSchema} from "./components/DictionaryPanel";
import IdentityUsersPanel from "./components/IdentityUsersPanel";
import KycPanel from "./components/KycPanel";
import Pagination from "./components/Pagination";
import PersonalDataPanel from "./components/PersonalDataPanel";
import PersonalDataConsentNotice from "./components/PersonalDataConsentNotice";
import RolesPanel from "./components/RolesPanel";
import {AuthContext} from "./contexts/AuthContext";
import {authBaseUrl, defaultAdminAppId, dictionaryApiUrl} from "./constants";
import {resolveDataFilter} from "./dataQuery";
import {paginateDictionaryDirectory} from "./dictionaryDirectory";
import {loadSupportedLanguages, normalizeLanguage, type Language} from "./i18n";
import {orderCatalogServices} from "./navigationOrder";
import {installSafariTabNavigation} from "./safariTabNavigation";
import {getInitialThemePreference, normalizeThemePreference, persistThemePreference, resolveThemePreference, type ThemePreferenceMode} from "./themePreference";
import type {AdminUser, Service} from "./types";
import {readSavedView, saveView} from "./viewState";

const iconTypes: Record<string, "overview" | "key" | "profile" | "book" | "kubernetes" | "admins" | "roles" | "audit"> = {
  overview: "overview",
  identity: "key",
  personal_data: "profile",
  kyc: "profile",
  dictionary: "book",
  kubernetes: "kubernetes",
  kms: "key",
  key_service: "key",
  keys: "key",
  administrators: "admins",
  roles: "roles",
  audit: "audit",
  logs: "audit",
};
const serviceIconClass = (key: string) => `service-symbol service-symbol--${key.replace(/[^a-z0-9_-]/gi, "-")}`;
const iconStrokeProps = {fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round"} as const;
const adminIcon = (key: string) => <AdminIcon type={iconTypes[key] || "overview"}/>;

function AdminIcon({type}: {type: "overview" | "key" | "profile" | "book" | "kubernetes" | "admins" | "roles" | "audit"}) {
  if (type === "key") return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="8" cy="8" r="4" {...iconStrokeProps}/><path d="M11 11l8 8M16 14l-2 2M19 17l-2 2" {...iconStrokeProps}/></svg>;
  if (type === "profile") return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="3" {...iconStrokeProps}/><circle cx="10" cy="11" r="2.2" {...iconStrokeProps}/><path d="M7.5 16c.7-1.8 4.3-1.8 5 0M15 10h2.5M15 14h2" {...iconStrokeProps}/></svg>;
  if (type === "book") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5.5c2.4-.9 4.7-.6 7 1.1v12c-2.3-1.7-4.6-2-7-1.1zM12 6.6c2.3-1.7 4.6-2 7-1.1v12c-2.4-.9-4.7-.6-7 1.1z" {...iconStrokeProps}/><path d="M12 6.6v12" {...iconStrokeProps}/></svg>;
  if (type === "kubernetes") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5l7.4 4.25v8.5L12 20.5l-7.4-4.25v-8.5z" {...iconStrokeProps}/><circle cx="12" cy="12" r="2.4" {...iconStrokeProps}/><path d="M12 6.5v3.1M12 14.4v3.1M7.2 9.2l2.7 1.5M14.1 13.3l2.7 1.5M16.8 9.2l-2.7 1.5M9.9 13.3l-2.7 1.5" {...iconStrokeProps}/></svg>;
  if (type === "admins") return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3" {...iconStrokeProps}/><path d="M4 18c.8-3.5 9.2-3.5 10 0" {...iconStrokeProps}/><path d="M15.5 7.5a2.5 2.5 0 010 5M16 15c1.8.3 3.3 1.2 4 3" {...iconStrokeProps}/></svg>;
  if (type === "roles") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.8l7 3v5.4c0 4.1-2.7 7.2-7 8-4.3-.8-7-3.9-7-8V6.8z" {...iconStrokeProps}/><path d="M9 12l2 2 4-4" {...iconStrokeProps}/></svg>;
  if (type === "audit") return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" {...iconStrokeProps}/><path d="M12 7.8v4.7l3 1.8" {...iconStrokeProps}/></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13.5l8-9 8 9" {...iconStrokeProps}/><path d="M7 13.5v5h10v-5" {...iconStrokeProps}/><path d="M10 18.5v-4h4v4" {...iconStrokeProps}/></svg>;
}

function MenuControlIcon({type}: {type: "menu" | "close" | "left" | "right" | "down"}) {
  if (type === "menu") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14M5 12h14M5 17h14" {...iconStrokeProps}/></svg>;
  if (type === "close") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" {...iconStrokeProps}/></svg>;
  if (type === "down") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 9.5L12 15l5.5-5.5" {...iconStrokeProps}/></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={type === "left" ? "M14.5 6.5L9 12l5.5 5.5" : "M9.5 6.5L15 12l-5.5 5.5"} {...iconStrokeProps}/></svg>;
}
const appId = defaultAdminAppId;
const personalDataBaseUrl = (process.env.REACT_APP_PD_BASE_URL || "https://qa-pd.id.express").replace(/\/$/, "");
const dictionaryApiBaseUrl = dictionaryApiUrl;
const technicalCollectionNames = new Set(["activity_logs", "authorization_tokens", "users"]);
const activityLogDefaultColumns = ["email", "service", "action", "createdAt"];
const identityAuthorizationTokensDefaultColumns = ["status", "accessTokenStatus", "refreshTokenStatus", "accessTokenExpAt", "refreshTokenExpAt"];
const kubernetesDefaultColumns: Record<string, string[]> = {
  namespaces: ["name", "status", "ageSeconds", "createdAt"],
  pods: ["namespace", "name", "status", "ready", "restarts", "node"],
  deployments: ["namespace", "name", "ready", "replicas", "available", "updated"],
  statefulsets: ["namespace", "name", "ready", "replicas", "available", "updated"],
  services: ["namespace", "name", "type", "clusterIp", "ports"],
  events: ["namespace", "type", "reason", "objectKind", "objectName", "eventTime"],
  metrics_nodes: ["name", "cpuMillicores", "memoryMiB", "timestamp"],
  metrics_pods: ["namespace", "name", "cpuMillicores", "memoryMiB", "containers", "timestamp"],
};
const sidebarStorageKey = "admin-id-express-sidebar-collapsed";
const navGroupsStorageKey = "admin-id-express-nav-groups-collapsed";
type TableFilterState = {field: string; value: string};
const tableFilterStorageKey = (dataset: string) => `admin-id-express-table-filter:${dataset}`;
const readTableFilter = (dataset: string): TableFilterState => {
  try {
    const value = JSON.parse(localStorage.getItem(tableFilterStorageKey(dataset)) || "{}");
    return {field: typeof value.field === "string" ? value.field : "*", value: typeof value.value === "string" ? value.value : ""};
  } catch { return {field: "*", value: ""}; }
};
const deploymentTheme = {...DEFAULT_IDEXPRESS_THEME, ...Object.fromEntries(Object.entries(themeTokensFromEnvironment(process.env)).filter(([, value]) => value))};
const deploymentThemeStyle = {
  "--theme-accent": deploymentTheme.accent, "--theme-light-background": deploymentTheme.lightBackground,
  "--theme-light-surface": deploymentTheme.lightSurface, "--theme-light-text": deploymentTheme.lightText,
  "--theme-light-muted": deploymentTheme.lightMuted, "--theme-light-border": deploymentTheme.lightBorder,
  "--theme-dark-background": deploymentTheme.darkBackground, "--theme-dark-surface": deploymentTheme.darkSurface,
  "--theme-dark-text": deploymentTheme.darkText, "--theme-dark-muted": deploymentTheme.darkMuted,
  "--theme-dark-border": deploymentTheme.darkBorder, "--theme-radius": deploymentTheme.radius,
} as React.CSSProperties;
const initialView = readSavedView();
const initialTableFilter = (): TableFilterState => initialView.filterValue !== undefined
  ? {field: initialView.filterField || "*", value: initialView.filterValue}
  : readTableFilter(`${initialView.section}:${initialView.resource || initialView.section}`);
const initialSidebarCollapsed = () => localStorage.getItem(sidebarStorageKey) === "true";
const initialCollapsedNavGroups = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(navGroupsStorageKey) || "[]");
    return Array.isArray(parsed) ? parsed.filter(item => typeof item === "string") : [];
  } catch {
    return [];
  }
};

const loginUrl = (language: Language) => {
  const authBase = new URL(authBaseUrl, location.origin).origin;
  const callback = new URL(location.href);
  callback.searchParams.delete("login");
  callback.searchParams.delete("logout");
  // A prior silent-SSO attempt already failed (id.express appended this on
  // its own interaction_required redirect) -- retrying with prompt=none
  // again would just bounce back-and-forth forever between this app and
  // id.express. Strip it from the referer we send onward and fall back to
  // an interactive login instead of silent.
  const silentSsoAlreadyFailed = callback.searchParams.get("error") === "interaction_required";
  callback.searchParams.delete("error");
  const promptParam = silentSsoAlreadyFailed ? "" : "&prompt=none";
  return `${authBase}/${language}/login/?appId=${encodeURIComponent(appId)}&referer=${encodeURIComponent(callback.toString())}${promptParam}`;
};

function App() {
  const {t, i18n} = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language) || "en";
  const [me, setMe] = useState<AdminUser | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarConsentUrl, setAvatarConsentUrl] = useState<string | null>(null);
  const [avatarConsentMessage, setAvatarConsentMessage] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [section, setSection] = useState(initialView.section);
  const [resource, setResource] = useState(initialView.resource);
  const [itemsPerPage, setItemsPerPage] = useState(initialView.pageSize || 25);
  const [rows, setRows] = useState<any[]>([]);
  const [tableFilter, setTableFilter] = useState<TableFilterState>(initialTableFilter);
  const [userTimeZone, setUserTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [pagination, setPagination] = useState<{currentPage: number; totalPages: number; count: number} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [themePreference, setThemePreferenceState] = useState<ThemePreferenceMode>(getInitialThemePreference);
  const [themeClockTick, setThemeClockTick] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
  const [collapsedNavGroups, setCollapsedNavGroups] = useState<string[]>(initialCollapsedNavGroups);
  const [activeLanguages, setActiveLanguages] = useState<Language[]>([]);
  const [languagesReady, setLanguagesReady] = useState(false);
  const [dictionarySchemas, setDictionarySchemas] = useState<Record<string, DictionaryFormSchema>>({});
  const [dictionaryCreateRequest, setDictionaryCreateRequest] = useState(0);
  const [personalDataSchema, setPersonalDataSchema] = useState<Record<string, any> | null>(null);
  const restoredView = useRef(false);
  const filterTimer = useRef<number | null>(null);
  const dataRequestSequence = useRef(0);
  const {AuthContextState, handleLogOut} = useContext(AuthContext);
  const {accessToken: token, user, isAuthenticated, isAuthLoading} = AuthContextState;
  const claims: any = user;
  const applyThemePreference = (value: string, persist = true) => {
    const normalized = normalizeThemePreference(value);
    if (!normalized) return;
    if (persist) persistThemePreference(normalized);
    setThemePreferenceState(normalized);
  };
  const savePreferences = (payload: {language?: string; themeMode?: ThemePreferenceMode}) => {
    if (!isAuthenticated) return;
    api.put("/preferences", payload).catch(() => undefined);
  };
  const can = (permission: string) => Boolean(me?.permissions.includes("*") || me?.permissions.includes(permission));
  const identityUsersStatus = section === "identity" && resource === "users" && tableFilter.field === "status"
    ? tableFilter.value
    : "";
  const sectionLabel = (value: string) => {
    if (value === "administrators") return t("administrators.title");
    if (value === "overview" || value === "roles" || value === "audit") return t(value);
    if (section === "kubernetes" && resource === value) return t(`kubernetes.resources.${value}`, {defaultValue: value});
    if (technicalCollectionNames.has(value)) return value;
    return value.replaceAll("_", " ");
  };
  const serviceLabel = (value: string, fallback: string) => {
    const keys: Record<string, string> = {
      identity: "identityService", personal_data: "personalDataService", dictionary: "dictionaryService", kyc: "kycService", kubernetes: "kubernetesService", logs: "serviceLogs",
    };
    return keys[value] ? t(keys[value]) : fallback;
  };
  const resourceLabel = (serviceKey: string, value: string) => {
    if (serviceKey === "kubernetes") return t(`kubernetes.resources.${value}`, {defaultValue: value});
    if (serviceKey === "identity") return t(`identity.resources.${value}`, {defaultValue: value.replaceAll("_", " ")});
    if (serviceKey === "personal_data") return t(`personalData.resources.${value}`, {defaultValue: value.replaceAll("_", " ")});
    if (serviceKey === "dictionary") return t(`dictionary.resources.${value}`, {defaultValue: value.replaceAll("_", " ")});
    if (serviceKey === "kyc") return t(`kyc.resources.${value}`, {defaultValue: value.replaceAll("_", " ")});
    if (serviceKey === "logs") return t(`logs.resources.${value}`, {defaultValue: value.replaceAll("_", " ")});
    return sectionLabel(value);
  };
  const isNavGroupCollapsed = (key: string) => collapsedNavGroups.includes(key);
  const toggleNavGroup = (key: string) => setCollapsedNavGroups(current => current.includes(key)
    ? current.filter(item => item !== key)
    : [...current, key]);
  const handleNavigationLogout = useCallback(async () => {
    await handleLogOut({});
    const logoutUrl = new URL(`${authBaseUrl.replace(/\/$/, "")}/${language}/logout/`);
    logoutUrl.searchParams.set("appId", appId);
    const callback = new URL(window.location.href);
    callback.searchParams.delete("login");
    callback.searchParams.delete("logout");
    logoutUrl.searchParams.set("referer", callback.toString());
    window.location.assign(logoutUrl.toString());
  }, [handleLogOut, language]);
  const {navigationConfig, navigationEventBus} = useIdExpressGlobalUserNavigation({
    isAuthenticated: isAuthenticated,
    user: {
      email: me?.email || claims.email || "",
      name: me?.displayName || claims.name || claims.email || t("administratorRole"),
      avatarUrl,
      roleLabel: me?.roles?.join(", ") || t("administratorRole"),
      isAdministrator: true,
    },
    currentLanguage: language,
    currentTheme: resolveThemePreference(themePreference),
    themeTokens: deploymentTheme,
    hiddenItems: ["wallet", "inbox"],
    showProfileLink: false,
    appId,
    refererUrl: window.location.origin,
    authBaseUrl,
    dictionaryApiBaseUrl,
    profileHref: `${personalDataBaseUrl}/${language}/personal_data`,
    onNavigate: (href: string) => { location.href = href; },
    onLogout: handleNavigationLogout,
    onLanguageChange: (value: string) => {
      const normalized = normalizeLanguage(value);
      if (activeLanguages.includes(normalized)) {
        i18n.changeLanguage(normalized);
        savePreferences({language: normalized});
      }
    },
    onThemeChange: (value: any) => {
      const normalized = normalizeThemePreference(value);
      if (!normalized) return;
      applyThemePreference(normalized);
      savePreferences({themeMode: normalized});
    },
  });

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setAvatarUrl(null);
      setAvatarConsentUrl(null);
      setAvatarConsentMessage(null);
      return;
    }
    let active = true;
    const refreshAvatar = async () => {
      try {
        const response = await api.get("/me/avatar-url", {
          params: {return_url: window.location.href},
          headers: {"Accept-Language": language},
        });
        if (!active) return;
        setAvatarUrl(typeof response.data?.downloadUrl === "string" ? response.data.downloadUrl : null);
        setAvatarConsentUrl(null);
        setAvatarConsentMessage(null);
      } catch (requestError: any) {
        if (!active) return;
        const status = requestError?.response?.status;
        const body = requestError?.response?.data;
        if (status === 403 && body?.code === "personal_data_consent_required") {
          setAvatarConsentUrl(trustedAvatarConsentUrl(body.consentUrl, personalDataBaseUrl));
          setAvatarConsentMessage(typeof body.message === "string" ? body.message.trim() : null);
          setAvatarUrl(null);
        } else if (status === 403 || status === 404 || status === 202) {
          setAvatarUrl(null);
          setAvatarConsentUrl(null);
          setAvatarConsentMessage(null);
        }
      }
    };
    void refreshAvatar();
    const onFocus = () => { void refreshAvatar(); };
    window.addEventListener("focus", onFocus);
    const interval = window.setInterval(onFocus, 240_000);
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
  }, [isAuthLoading, isAuthenticated, token, language]);

  useEffect(() => {
    if (themePreference !== "auto") return;
    const intervalId = window.setInterval(() => {
      setThemeClockTick(value => value + 1);
    }, 5 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [themePreference]);
  useEffect(() => { localStorage.setItem(sidebarStorageKey, sidebarCollapsed ? "true" : "false"); }, [sidebarCollapsed]);
  useEffect(() => { localStorage.setItem(navGroupsStorageKey, JSON.stringify(collapsedNavGroups)); }, [collapsedNavGroups]);
  useEffect(() => installSafariTabNavigation(), []);

  useEffect(() => {
    if (!mobile) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobile(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobile]);

  useEffect(() => {
    let active = true;
    loadSupportedLanguages(dictionaryApiBaseUrl)
      .then(values => {
        if (!active) return;
        const supported = values.length ? values : ["en"];
        setActiveLanguages(supported);
        if (!supported.includes(language)) i18n.changeLanguage(supported.includes("en") ? "en" : supported[0]);
      })
      .catch(() => {
        if (!active) return;
        setActiveLanguages(["en"]);
        i18n.changeLanguage("en");
      })
      .finally(() => { if (active) setLanguagesReady(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const persisted = persistLanguagePreference(language) || language;
    document.documentElement.lang = persisted;
    document.documentElement.dir = i18n.dir(persisted);
  }, [language, i18n]);

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      if (isAuthLoading) return;
      if (!languagesReady) return;
      if (!isAuthenticated) { location.replace(loginUrl(language)); return; }
      try {
        const [meResponse, catalogResponse] = await Promise.all([
          api.get("/me"),
          api.get("/catalog"),
        ]);
        const preferencesResponse = await api.get("/preferences").catch(() => ({data: {data: {}}}));
        if (active) {
          const preferences = preferencesResponse.data?.data || {};
          const preferredLanguage = normalizeLanguage(preferences.language);
          const preferredTheme = normalizeThemePreference(preferences.themeMode);
          if (preferences.timezone) {
            try {
              new Intl.DateTimeFormat("en", {timeZone: preferences.timezone}).format();
              setUserTimeZone(preferences.timezone);
            } catch { /* Keep the browser timezone when the profile value is invalid. */ }
          }
          if (preferredLanguage && activeLanguages.includes(preferredLanguage)) {
            i18n.changeLanguage(preferredLanguage);
          }
          if (preferredTheme) {
            applyThemePreference(preferredTheme);
          }
          setMe(meResponse.data);
          setServices(orderCatalogServices(catalogResponse.data.data || []));
        }
      } catch (requestError: any) {
        if (active) setError(t("accessDenied"));
      } finally { if (active) setLoading(false); }
    };
    bootstrap();
    return () => { active = false; };
    // Changing UI language must not repeat authentication.
  }, [token, isAuthenticated, isAuthLoading, languagesReady]);

  const navigation = useMemo(() => services.flatMap(service => service.resources.length ? [{key: service.key, label: serviceLabel(service.key, service.label), children: service.resources}] : []), [services, language]);
  const load = async (service: string, selectedResource: string, currentPage = 1, nextItemsPerPage = itemsPerPage, filterOverride?: TableFilterState) => {
    const requestSequence = ++dataRequestSequence.current;
    const dataset = `${service}:${selectedResource}`;
    const selectedFilter = filterOverride || (service === section && selectedResource === resource ? tableFilter : readTableFilter(dataset));
    const resolvedFilter = resolveDataFilter(service, selectedResource, selectedFilter);
    const sameDataset = service === section && selectedResource === resource;
    setTableFilter(selectedFilter);
    setItemsPerPage(nextItemsPerPage);
    saveView({
      section: service,
      resource: selectedResource,
      page: currentPage,
      pageSize: nextItemsPerPage,
      filterField: selectedFilter.field,
      filterValue: selectedFilter.value,
    });
    setSection(service); setResource(selectedResource); setMobile(false); setError("");
    if (!sameDataset || rows.length === 0) {
      setLoading(true);
      setRows([]);
      setPagination(null);
    }
    try {
      const params: Record<string, any> = {current_page: currentPage, items_per_page: nextItemsPerPage};
      if (service === "personal_data" && selectedResource === "records" && !personalDataSchema) {
        params.include_schema = true;
      }
      if (resolvedFilter.status) params.status = resolvedFilter.status;
      if (resolvedFilter.search) {
        if (service === "logs" && resolvedFilter.searchField === "level") params.level = resolvedFilter.search;
        else {
          params.search = resolvedFilter.search;
          if (resolvedFilter.searchField) params.search_field = resolvedFilter.searchField;
        }
      }
      const response = await api.get(`/data/${service}/${selectedResource}`, {params});
      if (requestSequence !== dataRequestSequence.current) return;
      if (service === "dictionary" && response.data?.formSchema) {
        setDictionarySchemas(current => ({
          ...current,
          [selectedResource]: response.data.formSchema,
        }));
      }
      if (service === "personal_data" && selectedResource === "records" && response.data?.schema) {
        setPersonalDataSchema(response.data.schema);
      }
      const payload = response.data?.data ?? response.data;
      setRows(Array.isArray(payload) ? payload : [payload]);
      const totalPages = Number(response.data?.total_pages || 0);
      setPagination(totalPages ? {currentPage: Number(response.data?.current_page || currentPage), totalPages, count: Number(response.data?.count || 0)} : null);
    } catch (requestError: any) {
      if (requestSequence === dataRequestSequence.current) setError(t("unableLoad"));
    } finally {
      if (requestSequence === dataRequestSequence.current) setLoading(false);
    }
  };
  const loadDictionaryDirectory = (
    currentPage = 1,
    nextItemsPerPage = itemsPerPage,
    filterOverride?: TableFilterState,
  ) => {
    ++dataRequestSequence.current;
    const dataset = "dictionary:create";
    const selectedFilter = filterOverride
      || (section === "dictionary" && resource === "create"
        ? tableFilter
        : readTableFilter(dataset));
    const dictionary = services.find(item => item.key === "dictionary");
    const directoryRows = (dictionary?.resources || [])
      .filter(item => item.key !== "create")
      .map(item => ({
        type: item.key,
        name: resourceLabel("dictionary", item.key),
      }));
    const page = paginateDictionaryDirectory(
      directoryRows,
      selectedFilter,
      currentPage,
      nextItemsPerPage,
    );
    setTableFilter(selectedFilter);
    setItemsPerPage(nextItemsPerPage);
    setSection("dictionary");
    setResource("create");
    setRows(page.data);
    setPagination({
      currentPage: page.currentPage,
      totalPages: page.totalPages,
      count: page.count,
    });
    setLoading(false);
    setError("");
    setMobile(false);
    saveView({
      section: "dictionary",
      resource: "create",
      page: page.currentPage,
      pageSize: nextItemsPerPage,
      filterField: selectedFilter.field,
      filterValue: selectedFilter.value,
    });
  };
  const changeIdentityUsersStatus = (status: string) => {
    const nextFilter = status ? {field: "status", value: status} : {field: "*", value: ""};
    setTableFilter(nextFilter);
    localStorage.setItem(tableFilterStorageKey("identity:users"), JSON.stringify(nextFilter));
    load("identity", "users", 1, itemsPerPage, nextFilter);
  };
  const openResource = (service: string, selectedResource: string) => {
    if (service === "dictionary" && selectedResource === "create") {
      loadDictionaryDirectory(1, itemsPerPage);
      return;
    }
    if (selectedResource === "cache") {
      setSection(service); setResource(selectedResource); setRows([]); setPagination(null); setLoading(false); setError(""); setMobile(false);
      saveView({section: service, resource: selectedResource, page: 1, pageSize: itemsPerPage});
      return;
    }
    load(service, selectedResource);
  };
  const saveDictionary = async (selectedResource: string, payload: Record<string, any>, creating: boolean) => {
    await api.request({method: creating ? "POST" : "PUT", url: `/data/dictionary/${creating ? "create" : selectedResource}`, data: payload});
    const target = creating ? payload.type : selectedResource;
    await load("dictionary", target, 1, itemsPerPage);
  };
  const deleteDictionary = async (selectedResource: string, record: Record<string, any>) => {
    await api.delete(`/data/dictionary/${selectedResource}`, {data: {_id: record._id}});
    await load("dictionary", selectedResource, pagination?.currentPage || 1, itemsPerPage);
  };
  const findDictionaryParents = useCallback(async (type: string, search: string) => {
    if (!type) return [];
    const response = await api.get(`/data/dictionary/${type}`, {
      params: {current_page: 1, items_per_page: 25, ...(search.trim() ? {search: search.trim()} : {})},
    });
    const records = Array.isArray(response.data?.data) ? response.data.data : [];
    return records.map((record: Record<string, any>) => {
      const fallbackValue = Object.values(record.value || {}).find((value: any) => value?.name) as any;
      return {
        id: String(record._id || record.id),
        key: String(record.key || ""),
        type: String(record.type || type),
        name: String(
          record.value?.[language]?.name
          || record.value?.default?.name
          || fallbackValue?.name
          || record.key
          || record._id,
        ),
      };
    });
  }, [language]);
  const loadDictionarySchema = async (type: string): Promise<DictionaryFormSchema> => {
    if (dictionarySchemas[type]) return dictionarySchemas[type];
    const response = await api.get(`/data/dictionary/${type}`, {
      params: {current_page: 1, items_per_page: 1},
    });
    const formSchema = (response.data?.formSchema || {
      type,
      fields: [],
      metadataFields: [],
    }) as DictionaryFormSchema;
    setDictionarySchemas(current => ({...current, [type]: formSchema}));
    return formSchema;
  };
  const reviewKyc = async (record: Record<string, any>, status: "verified" | "rejected", reviewComment: string) => {
    await api.put("/data/personal_data/verification", {recordId: record._id, status, reviewComment});
    await load("personal_data", "verification", pagination?.currentPage || 1, itemsPerPage);
  };
  const updateIdentityUserStatus = (userId: string, status: string, response: Record<string, any>) => {
    const changedAt = response?.statusChangedAt || response?.data?.statusChangedAt || new Date().toISOString();
    setRows(current => current.map(row => String(row._id || row.id || row.userId) === userId
      ? {...row, disabled: status === "blocked", status, accountStatusChangedAt: changedAt}
      : row));
    if (identityUsersStatus && identityUsersStatus !== status) {
      setRows(current => current.filter(row => String(row._id || row.id || row.userId) !== userId));
    }
  };
  const loadLocal = async (name: string, currentPage = 1, nextItemsPerPage = itemsPerPage, filterOverride?: TableFilterState) => {
    const requestSequence = ++dataRequestSequence.current;
    const dataset = `${name}:${name}`;
    const selectedFilter = filterOverride || (name === section && !resource ? tableFilter : readTableFilter(dataset));
    const sameDataset = name === section && !resource;
    setTableFilter(selectedFilter);
    setItemsPerPage(nextItemsPerPage);
    saveView({section: name, resource: "", page: currentPage, pageSize: nextItemsPerPage, filterField: selectedFilter.field, filterValue: selectedFilter.value});
    setSection(name); setResource(""); setMobile(false); setError("");
    if (!sameDataset || rows.length === 0) {
      setRows([]);
      setPagination(null);
    }
    if (name === "administrators" || name === "roles") { setLoading(false); return; }
    if (!sameDataset || rows.length === 0) setLoading(true);
    try {
      const params: Record<string, any> = {current_page: currentPage, items_per_page: nextItemsPerPage};
      if (selectedFilter.value.trim()) {
        params.search = selectedFilter.value.trim();
        if (selectedFilter.field !== "*") params.search_field = selectedFilter.field;
      }
      const response = await api.get(`/${name}`, {params});
      if (requestSequence !== dataRequestSequence.current) return;
      setRows(response.data.data || []);
      const totalPages = Number(response.data?.total_pages || 0);
      const count = Number(response.data?.count || 0);
      setPagination(count ? {
        currentPage: Number(response.data?.current_page || currentPage),
        totalPages,
        count,
      } : null);
    } catch (requestError: any) {
      if (requestSequence === dataRequestSequence.current) setError(t("unableLoad"));
    } finally {
      if (requestSequence === dataRequestSequence.current) setLoading(false);
    }
  };
  const changeServerFilter = (next: TableFilterState) => {
    setTableFilter(next);
    const dataset = `${section}:${resource || section}`;
    localStorage.setItem(tableFilterStorageKey(dataset), JSON.stringify(next));
    saveView({
      section,
      resource,
      page: 1,
      pageSize: itemsPerPage,
      filterField: next.field,
      filterValue: next.value,
    });
    if (filterTimer.current !== null) window.clearTimeout(filterTimer.current);
    filterTimer.current = window.setTimeout(() => {
      if (section === "dictionary" && resource === "create") {
        loadDictionaryDirectory(1, itemsPerPage, next);
      } else if (resource) load(section, resource, 1, itemsPerPage, next);
      else loadLocal(section, 1, itemsPerPage, next);
    }, 350);
  };
  useEffect(() => () => { if (filterTimer.current !== null) window.clearTimeout(filterTimer.current); }, []);
  useEffect(() => {
    if (!me || restoredView.current) return;
    restoredView.current = true;
    const requested = readSavedView();
    if (requested.section === "overview" && can("admin.overview.view")) { saveView({section: "overview", resource: "", page: 1}); return; }
    if (requested.section === "administrators" && can("admin.users.view") && can("admin.users.read")) { saveView({section: requested.section, resource: "", page: 1}); return; }
    if (requested.section === "roles" && can("admin.roles.view") && can("admin.roles.read")) { loadLocal("roles", requested.page, requested.pageSize || itemsPerPage); return; }
    if (requested.section === "audit" && can("admin.audit.view") && can("admin.audit.read")) { loadLocal("audit", requested.page, requested.pageSize || itemsPerPage); return; }
    const service = services.find(item => item.key === requested.section);
    if (service?.resources.some(item => item.key === requested.resource)) {
      if (requested.section === "dictionary" && requested.resource === "create") {
        loadDictionaryDirectory(
          requested.page,
          requested.pageSize || itemsPerPage,
          {
            field: requested.filterField || "*",
            value: requested.filterValue || "",
          },
        );
      }
      else if (requested.resource === "cache") {
        setSection(requested.section); setResource("cache"); setRows([]); setPagination(null); setLoading(false); setError("");
      } else load(requested.section, requested.resource, requested.page, requested.pageSize || itemsPerPage);
      return;
    }
    if (can("admin.overview.view")) { setSection("overview"); setResource(""); setRows([]); setPagination(null); saveView({section: "overview", resource: "", page: 1}); return; }
    const firstService = services.find(item => item.resources.length > 0);
    if (firstService) { load(firstService.key, firstService.resources[0].key); return; }
    if (can("admin.users.view") && can("admin.users.read")) { setSection("administrators"); setResource(""); setRows([]); setPagination(null); saveView({section: "administrators", resource: "", page: 1}); return; }
    if (can("admin.roles.view") && can("admin.roles.read")) { loadLocal("roles", 1); return; }
    if (can("admin.audit.view") && can("admin.audit.read")) { loadLocal("audit", 1); return; }
    setError(t("accessDenied"));
  }, [me, services]);
  if (loading && !me) return <div className="splash"><div className="orb"/><p>{t("opening")}</p></div>;
  if (error && !me) return <div className="denied"><b>{t("accessRestricted")}</b><p>{error}</p><a href={loginUrl(language)}>{t("signIn")}</a></div>;
  if (!me) return null;

  const localSections = [
    can("admin.users.view") && can("admin.users.read") && "administrators",
    can("admin.roles.view") && can("admin.roles.read") && "roles",
  ].filter(Boolean) as string[];
  const canViewCentralAudit = can("admin.audit.view") && can("admin.audit.read");
  const direction = i18n.dir(language);
  void themeClockTick;
  const dark = resolveThemePreference(themePreference) === "dark";
  const appClasses = ["app", dark ? "dark" : "light", sidebarCollapsed ? "sidebar-collapsed" : ""].filter(Boolean).join(" ");
  const collapseDirection: "left" | "right" = direction === "rtl"
    ? (sidebarCollapsed ? "left" : "right")
    : (sidebarCollapsed ? "right" : "left");
  const activeService = services.find(item => item.key === section);
  const activityRows = section === "audit" || resource === "activity_logs"
    ? normalizeActivityLogRows(rows)
    : rows;
  const activeSectionLabel = activeService ? serviceLabel(activeService.key, activeService.label) : sectionLabel(section);

  const dictionaryService = services.find(item => item.key === "dictionary");
  const dictionaryCanCreate = Boolean(dictionaryService?.resources.find(item => item.key === "create")?.methods.includes("POST"));

  return <div className={appClasses} style={deploymentThemeStyle} dir={direction}>
    <a className="skip-link" href="#admin-main-content">{t("skipToContent")}</a>
    {mobile && <button type="button" className="sidebar-backdrop" aria-label={t("administrators.close")} onClick={() => setMobile(false)}/>}
    <aside id="admin-sidebar" className={mobile ? "sidebar open" : "sidebar"}>
      <div className="brand"><span>ID</span><div><b>Express</b><small>{t("controlPlane")}</small></div><button type="button" className="sidebar-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={() => setMobile(false)}><MenuControlIcon type="close"/></button></div>
      <nav>
        {can("admin.overview.view") && <button type="button" title={t("overview")} onClick={() => { setSection("overview"); setResource(""); setRows([]); setPagination(null); setMobile(false); saveView({section: "overview", resource: "", page: 1}); }} className={section === "overview" ? "active" : ""}><i className={serviceIconClass("overview")}>{adminIcon("overview")}</i>{t("overview")}</button>}
        {navigation.map(service => {
          const groupId = `admin-nav-group-${service.key}`;
          const collapsed = isNavGroupCollapsed(service.key);
          return <div className={`navgroup${collapsed ? " collapsed" : ""}`} key={service.key}>
            <button type="button" className="navgroup-toggle" title={service.label} aria-expanded={!collapsed} aria-controls={groupId} onClick={() => toggleNavGroup(service.key)}><span>{service.label}</span><b><MenuControlIcon type={collapsed ? (direction === "rtl" ? "left" : "right") : "down"}/></b></button>
            {!collapsed && <div id={groupId} className="navgroup-items">{service.children.map(item => <button type="button" key={item.key} title={resourceLabel(service.key, item.key)} className={section === service.key && resource === item.key ? "active" : ""} onClick={() => openResource(service.key, item.key)}><i className={serviceIconClass(service.key)}>{adminIcon(service.key)}</i>{resourceLabel(service.key, item.key)}</button>)}</div>}
          </div>;
        })}
        {localSections.length > 0 && (() => {
          const groupKey = "administration";
          const groupId = "admin-nav-group-administration";
          const collapsed = isNavGroupCollapsed(groupKey);
          return <div className={`navgroup${collapsed ? " collapsed" : ""}`}>
            <button type="button" className="navgroup-toggle" title={t("administration")} aria-expanded={!collapsed} aria-controls={groupId} onClick={() => toggleNavGroup(groupKey)}><span>{t("administration")}</span><b><MenuControlIcon type={collapsed ? (direction === "rtl" ? "left" : "right") : "down"}/></b></button>
            {!collapsed && <div id={groupId} className="navgroup-items">{localSections.map(item => <button type="button" key={item} title={sectionLabel(item)} className={section === item ? "active" : ""} onClick={() => loadLocal(item)}><i className={serviceIconClass(item)}>{adminIcon(item)}</i>{sectionLabel(item)}</button>)}</div>}
          </div>;
        })()}
        {canViewCentralAudit && (() => {
          const groupKey = "activity_log";
          const groupId = "admin-nav-group-activity-log";
          const collapsed = isNavGroupCollapsed(groupKey);
          return <div className={`navgroup audit-navgroup${collapsed ? " collapsed" : ""}`}>
            <button type="button" className="navgroup-toggle" title={t("audit")} aria-expanded={!collapsed} aria-controls={groupId} onClick={() => toggleNavGroup(groupKey)}><span>{t("audit")}</span><b><MenuControlIcon type={collapsed ? (direction === "rtl" ? "left" : "right") : "down"}/></b></button>
            {!collapsed && <div id={groupId} className="navgroup-items"><button type="button" title={t("audit")} className={section === "audit" ? "active" : ""} onClick={() => loadLocal("audit")}><i className={serviceIconClass("audit")}>{adminIcon("audit")}</i>{t("audit")}</button></div>}
          </div>;
        })()}
      </nav>
    </aside>
    <button type="button" className="sidebar-rail-toggle" title={t("menu")} aria-label={t("menu")} aria-expanded={!sidebarCollapsed} aria-controls="admin-sidebar" onClick={() => setSidebarCollapsed(value => !value)}><MenuControlIcon type={collapseDirection}/></button>
    <main id="admin-main-content" tabIndex={-1}>
      <header><button type="button" className="hamburger" title={t("menu")} aria-label={t("menu")} aria-expanded={mobile} aria-controls="admin-sidebar" onClick={() => setMobile(!mobile)}><MenuControlIcon type={mobile ? "close" : "menu"}/></button><div className="title"><small>{t("adminConsole")}</small><h1>{resource ? resourceLabel(section, resource) : sectionLabel(section)}</h1></div><div className="top-actions"><GlobalUserNavigation config={navigationConfig} eventBus={navigationEventBus}/></div></header>
      <section className="content">
        {avatarConsentUrl && <PersonalDataConsentNotice
          applicationLabel={t("adminConsole")}
          resourceLabel={`${t("personalData.categories.profile")} · data.profile.avatar`}
          permissionLabel={t("rolesPanel.actions.read")}
          message={avatarConsentMessage || t("accessRestricted")}
          consentUrl={avatarConsentUrl}
        />}
        <Breadcrumbs
          sectionLabel={activeSectionLabel}
          resourceLabel={resource ? resourceLabel(section, resource) : undefined}
          onHome={() => { setSection("overview"); setResource(""); setRows([]); setPagination(null); saveView({section: "overview", resource: "", page: 1}); }}
          actions={section === "dictionary" && resource !== "create" && dictionaryCanCreate ?
            <button type="button" className="primary breadcrumbs-add" title={t("dictionary.addRecord")} onClick={() => setDictionaryCreateRequest(value => value + 1)}>+ {t("dictionary.addRecord")}</button> :
            undefined}
        />
        {section === "overview" ? <Dashboard me={me} services={services} userTimeZone={userTimeZone}/> : section === "administrators" ?
          <AdministratorsPanel currentUser={me} permissions={me.permissions}/> : section === "roles" ?
          <RolesPanel permissions={me.permissions}/> : <>
            {error && <div className="alert" role="alert">{error}</div>}
            {!loading && section === "kubernetes" && <KubernetesMetricsCharts rows={rows} resource={resource}/>}
            {loading ? <div className="empty compact"><span className="mini-spinner"/><p>{t("opening")}</p></div> : resource === "cache" ?
              <CachePanel service={section} scopes={navigation.find(item => item.key === section)?.children.find(item => item.key === "cache")?.cacheScopes || []}/> : section === "identity" && resource === "users" ?
              <IdentityUsersPanel rows={rows} canUpdate={can("identity.users.update")} statusFilter={identityUsersStatus} onStatusFilterChange={changeIdentityUsersStatus} onStatusChanged={updateIdentityUserStatus} userTimeZone={userTimeZone} totalCount={pagination?.count} serverFilter={{...tableFilter, onChange: changeServerFilter}}/> :
              section === "dictionary" ? <DictionaryPanel key={`${section}:${resource}`} rows={rows} datasetKey={`${section}:${resource}`} resource={resource} categories={(dictionaryService?.resources || []).map(item => item.key).filter(item => item !== "create")} languages={activeLanguages} methods={dictionaryService?.resources.find(item => item.key === resource)?.methods || []} canCreate={dictionaryCanCreate} createRequest={dictionaryCreateRequest} schemas={dictionarySchemas} onLoadSchema={loadDictionarySchema} onOpenCategory={type => openResource("dictionary", type)} onSave={saveDictionary} onDelete={deleteDictionary} onFindParents={findDictionaryParents} userTimeZone={userTimeZone} totalCount={pagination?.count} serverFilter={{...tableFilter, onChange: changeServerFilter}}/> :
              section === "personal_data" && resource === "verification" ? <KycPanel rows={rows} datasetKey="personal_data:verification" canReview={can("personal_data.verification.update")} onReview={reviewKyc} userTimeZone={userTimeZone} totalCount={pagination?.count} serverFilter={{...tableFilter, onChange: changeServerFilter}}/> :
              section === "personal_data" && resource === "records" ? <PersonalDataPanel rows={rows} datasetKey="personal_data:records:v2" schema={personalDataSchema || undefined} userTimeZone={userTimeZone} totalCount={pagination?.count} serverFilter={{...tableFilter, onChange: changeServerFilter}}/> :
              <DataTable rows={activityRows} datasetKey={section === "audit" ? "audit:central-activity-log:v3" : resource === "activity_logs" ? `central-activity-log:${section}:v3` : `${section}:${resource || section}`} defaultColumns={section === "audit" || resource === "activity_logs" ? activityLogDefaultColumns : section === "kubernetes" ? kubernetesDefaultColumns[resource] : section === "logs" ? ["createdAt", "level", "message", "source", "logger"] : section === "identity" && resource === "authorization_tokens" ? identityAuthorizationTokensDefaultColumns : undefined} userTimeZone={userTimeZone} totalCount={pagination?.count} serverFilter={{...tableFilter, onChange: changeServerFilter}}/>}
            {pagination && <Pagination {...pagination} pageSize={itemsPerPage} disabled={loading}
              onPageChange={page => section === "dictionary" && resource === "create"
                ? loadDictionaryDirectory(page, itemsPerPage)
                : resource ? load(section, resource, page) : loadLocal(section, page)}
              onPageSizeChange={pageSize => section === "dictionary" && resource === "create"
                ? loadDictionaryDirectory(1, pageSize)
                : resource ? load(section, resource, 1, pageSize) : loadLocal(section, 1, pageSize)}/>}
          </>}
      </section>
    </main>
  </div>;
}

type OverviewData = {
  identity?: Record<string, number | string>;
  kyc?: {pendingRequests?: number};
  recentActivity?: Array<Record<string, any>>;
  warnings?: string[];
};

const overviewFilters = ["all", "authorization", "updates", "deletions", "creations", "errors"] as const;

function Dashboard({me, services, userTimeZone}: {me: AdminUser; services: Service[]; userTimeZone: string}) {
  const {t, i18n} = useTranslation();
  const [data, setData] = useState<OverviewData>({});
  const [activityFilter, setActivityFilter] = useState<(typeof overviewFilters)[number]>("all");
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  useEffect(() => {
    let active = true;
    setDashboardLoading(true);
    setDashboardError("");
    api.get("/overview", {params: {activity: activityFilter}})
      .then(response => { if (active) setData(response.data?.data || {}); })
      .catch(() => { if (active) setDashboardError(t("overviewDashboard.unavailable")); })
      .finally(() => { if (active) setDashboardLoading(false); });
    return () => { active = false; };
  }, [activityFilter, t]);
  const identity = data.identity || {};
  const number = (key: string) => typeof identity[key] === "number" ? identity[key] as number : "—";
  const formatTime = (value: unknown) => {
    const date = new Date(String(value || ""));
    if (Number.isNaN(date.getTime())) return "—";
    try {
      return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || "en", {
        hour: "2-digit", minute: "2-digit", timeZone: userTimeZone,
      }).format(date);
    } catch { return date.toISOString().slice(11, 16); }
  };
  const labels: Record<string, string> = {identity: "identityService", personal_data: "personalDataService", dictionary: "dictionaryService", kyc: "kycService", kubernetes: "kubernetesService", logs: "serviceLogs"};
  const eventService = (value: unknown) => {
    const key = String(value || "");
    return labels[key] ? t(labels[key]) : key === "admin" ? t("administration") : key || "—";
  };
  const eventResource = (service: unknown, value: unknown) => {
    const serviceKey = String(service || "");
    const resourceKey = String(value || "");
    if (!resourceKey) return "";
    if (serviceKey === "personal_data") return t(`personalData.resources.${resourceKey}`, {defaultValue: resourceKey.replaceAll("_", " ")});
    if (serviceKey === "identity") return t(`identity.resources.${resourceKey}`, {defaultValue: resourceKey.replaceAll("_", " ")});
    if (serviceKey === "dictionary") return t(`dictionary.resources.${resourceKey}`, {defaultValue: resourceKey.replaceAll("_", " ")});
    if (serviceKey === "logs") return t(`logs.resources.${resourceKey}`, {defaultValue: resourceKey.replaceAll("_", " ")});
    if (serviceKey === "admin" && resourceKey === "users") return t("administrators.title");
    if (serviceKey === "admin" && resourceKey === "roles") return t("roles");
    return resourceKey.replaceAll("_", " ");
  };
  return <>
    <div className="hero"><div><span className="eyebrow">{t("systemOverview")}</span><h2>{t("greeting")}, {me.displayName || me.email}</h2><p>{t("workspace")}</p></div><div className="live"><span/>{t("connected")}</div></div>
    {dashboardError && <div className="alert" role="alert">{dashboardError}</div>}
    {data.warnings?.length ? <div className="overview-warning" role="status">{t("overviewDashboard.partialData")}: {data.warnings.join(", ")}</div> : null}
    <div className={`metrics overview-metrics${dashboardLoading ? " loading" : ""}`}>
      <Metric label={t("overviewDashboard.totalUsers")} value={number("totalUsers")} trend={t("overviewDashboard.accounts")}/>
      <Metric label={t("overviewDashboard.activeUsers")} value={number("activeUsers")} trend={t("overviewDashboard.verifiedAccounts")}/>
      <Metric label={t("overviewDashboard.newToday")} value={number("newUsersToday")} trend={t("overviewDashboard.utcPeriod")}/>
      <Metric label={t("overviewDashboard.newWeek")} value={number("newUsersWeek")} trend={t("overviewDashboard.utcPeriod")}/>
      <Metric label={t("overviewDashboard.onlineUsers")} value={number("onlineUsers")} trend={t("overviewDashboard.liveRefresh")}/>
      <Metric label={t("overviewDashboard.activeSessions")} value={number("activeSessions")} trend={t("overviewDashboard.liveRefresh")}/>
      <Metric label={t("overviewDashboard.activeDevices")} value={number("activeDevices")} trend={t("overviewDashboard.uniqueDevices")}/>
      <Metric label={t("overviewDashboard.pendingKyc")} value={typeof data.kyc?.pendingRequests === "number" ? data.kyc.pendingRequests : "—"} trend={t("overviewDashboard.requiresReview")}/>
    </div>
    <section className="recent-activity">
      <div className="recent-activity-heading"><div><span className="eyebrow">{t("overviewDashboard.monitoring")}</span><h2>{t("overviewDashboard.recentActivity")}</h2></div><div className="activity-filters" role="group" aria-label={t("overviewDashboard.activityFilter")}>{overviewFilters.map(filter => <button type="button" title={t(`overviewDashboard.filters.${filter}`)} className={filter === activityFilter ? "active" : ""} onClick={() => setActivityFilter(filter)} key={filter}>{t(`overviewDashboard.filters.${filter}`)}</button>)}</div></div>
      {dashboardLoading ? <div className="empty compact"><span className="mini-spinner"/></div> : data.recentActivity?.length ? <ol className="activity-stream">{data.recentActivity.map(event => {
        const action = String(event.action || "").toLowerCase();
        const resourceName = eventResource(event.service, event.resource);
        const actorEmail = event.email || event.metadata?.actor_email || event.metadata?.details?.actorEmail || event.metadata?.details?.email;
        const subject = event.subjectEmail || event.metadata?.subject_email || event.userId || event.subjectId;
        const actor = actorEmail || event.actorId || "—";
        const attribution = subject && String(subject) !== String(actor) ? `${actor} → ${subject}` : actor;
        return <li key={event.id || event.eventId}><time>{formatTime(event.createdAt)}</time><div><b>{attribution}</b><span>{t(`activityEvents.${action}`, {defaultValue: action || "—"})} · {eventService(event.service)}{resourceName ? ` — ${resourceName}` : ""}</span></div><em className={Number(event.statusCode) >= 400 ? "error" : "success"}>{event.statusCode || "—"}</em></li>;
      })}</ol> : <div className="empty compact"><b>{t("noRecords")}</b><p>{t("noVisibleData")}</p></div>}
    </section>
    <div className="service-grid">{services.map((service, index) => <article key={service.key} style={{"--accent": `${210 + index * 35}`} as any}><div className={`service-icon ${serviceIconClass(service.key)}`}>{adminIcon(service.key)}</div><div><h3>{labels[service.key] ? t(labels[service.key]) : service.label}</h3><p>{service.resources.length} {t("managedResources")}</p></div><span className="status">{t("online")}</span></article>)}</div>
  </>;
}
function Metric({label, value, trend}: {label: string; value: number | string; trend: string}) { return <article className="metric"><small>{label}</small><strong>{value}</strong><span>{trend}</span></article>; }

function KubernetesMetricsCharts({rows, resource}: {rows: Array<Record<string, any>>; resource: string}) {
  const {t} = useTranslation();
  if (!["metrics_nodes", "metrics_pods"].includes(resource)) return null;
  const measurableRows = rows
    .map(row => ({
      label: String(resource === "metrics_pods" ? `${row.namespace || "default"}/${row.name || "—"}` : row.name || "—"),
      cpu: Number(row.cpuMillicores || 0),
      memory: Number(row.memoryMiB || 0),
    }))
    .filter(row => row.cpu > 0 || row.memory > 0);
  if (!measurableRows.length) {
    return <div className="k8s-metrics-empty">{t("kubernetes.metrics.unavailable")}</div>;
  }
  const cpuRows = [...measurableRows].sort((a, b) => b.cpu - a.cpu).slice(0, 8);
  const memoryRows = [...measurableRows].sort((a, b) => b.memory - a.memory).slice(0, 8);
  return <div className="k8s-metrics-grid">
    <KubernetesMetricChart title={t("kubernetes.metrics.cpu")} unit="m" rows={cpuRows} field="cpu"/>
    <KubernetesMetricChart title={t("kubernetes.metrics.memory")} unit="MiB" rows={memoryRows} field="memory"/>
  </div>;
}

function KubernetesMetricChart({title, unit, rows, field}: {title: string; unit: string; rows: Array<{label: string; cpu: number; memory: number}>; field: "cpu" | "memory"}) {
  const max = Math.max(...rows.map(row => row[field]), 1);
  return <article className="k8s-chart-card">
    <div className="k8s-chart-head"><b>{title}</b><span>Top {rows.length}</span></div>
    <div className="k8s-chart-list">
      {rows.map(row => <div className="k8s-chart-row" key={`${field}:${row.label}`}>
        <span title={row.label}>{row.label}</span>
        <div className="k8s-chart-track"><i style={{width: `${Math.max(4, (row[field] / max) * 100)}%`}}/></div>
        <strong>{row[field].toLocaleString(undefined, {maximumFractionDigits: 1})} {unit}</strong>
      </div>)}
    </div>
  </article>;
}
export default App;
